import type { FitnessState } from "./types";

export const demoState: FitnessState = {
  selectedDate: "2026-09-22",
  user: {
    name: "Mathieu",
    email: "mathieu@exemple.fr",
  },
  profile: { ageYears: 37, heightCm: 178, startingWeightKg: 80.2 },
  calories: {
    consumed: 1480,
    target: 2200,
    burned: 540,
    budget: 2740,
    remaining: 1260,
    includeBurned: true,
  },
  macroTargets: { protein: 110, carbs: 245, fat: 73 },
  nutrients: [
    { id: "protein", label: "Protéines", current: 82, target: 110, unit: "g", coverage: 100 },
    { id: "fiber", label: "Fibres", current: 19, target: 25, unit: "g", coverage: 94 },
    { id: "vitaminA", label: "Vitamine A", current: 510, target: 800, unit: "µg", coverage: 82 },
    { id: "vitaminC", label: "Vitamine C", current: 61, target: 80, unit: "mg", coverage: 82 },
    { id: "vitaminD", label: "Vitamine D", current: 2.1, target: 5, unit: "µg", coverage: 45 },
    { id: "vitaminE", label: "Vitamine E", current: 7.4, target: 12, unit: "mg", coverage: 63 },
    { id: "calcium", label: "Calcium", current: 620, target: 800, unit: "mg", coverage: 76 },
    { id: "iron", label: "Fer", current: 8.6, target: 14, unit: "mg", coverage: 73 },
    { id: "magnesium", label: "Magnésium", current: 228, target: 375, unit: "mg", coverage: 68 },
    { id: "potassium", label: "Potassium", current: 1510, target: 2000, unit: "mg", coverage: 80 },
    { id: "zinc", label: "Zinc", current: 6.8, target: 10, unit: "mg", coverage: 71 },
  ],
  objectives: [
    { id: "weight", type: "weight", label: "Poids cible", start: 80.2, current: 78.4, target: 76, unit: "kg", direction: "reach", tone: "sage", status: "active", history: [{ target: 76, effectiveFrom: "2026-09-01" }] },
    { id: "waist", type: "measurement", label: "Tour de taille", start: 91, current: 88, target: 84, unit: "cm", direction: "reach", tone: "gold", status: "active" },
    { id: "calories", type: "calories", label: "Apport quotidien", current: 1480, target: 2200, unit: "kcal", direction: "max", tone: "ink", status: "active", period: "day" },
    { id: "steps", type: "steps", label: "Pas quotidiens", current: 6420, target: 8000, unit: "pas", direction: "reach", tone: "sage", status: "active", period: "day" },
  ],
  sessions: [
    {
      id: "swim-1",
      programId: "program-1",
      title: "Endurance technique",
      sport: "Natation",
      date: "2026-09-22",
      start: "18:30",
      durationMinutes: 55,
      location: "Piscine de Bonnevoie",
      status: "planned",
      steps: [
        { id: "warmup", label: "Échauffement", detail: "300 m nage libre", kind: "distance", distanceMeters: 300 },
        { id: "drills", label: "Technique", detail: "8 × 50 m · récupération 20 s", kind: "interval", sets: 8, distanceMeters: 50, restSeconds: 20 },
        { id: "main", label: "Bloc principal", detail: "4 × 200 m · allure régulière", kind: "interval", sets: 4, distanceMeters: 200 },
        { id: "cooldown", label: "Retour au calme", detail: "200 m souple", kind: "distance", distanceMeters: 200 },
      ],
    },
    {
      id: "strength-1",
      programId: "program-1",
      title: "Force générale",
      sport: "Musculation",
      date: "2026-09-24",
      start: "18:00",
      durationMinutes: 65,
      location: "Salle",
      status: "planned",
      steps: [
        { id: "squat", label: "Squat", detail: "4 × 6 · 70 kg", kind: "sets", sets: 4, reps: 6, loadKg: 70, restSeconds: 120 },
        { id: "bench", label: "Développé couché", detail: "4 × 8 · 52,5 kg", kind: "sets", sets: 4, reps: 8, loadKg: 52.5, restSeconds: 90 },
        { id: "row", label: "Rowing", detail: "3 × 10 · 45 kg", kind: "sets", sets: 3, reps: 10, loadKg: 45, restSeconds: 75 },
      ],
    },
    {
      id: "run-1",
      programId: "program-1",
      title: "Sortie facile",
      sport: "Course",
      date: "2026-09-26",
      start: "09:30",
      durationMinutes: 45,
      location: "Parc de Merl",
      status: "planned",
      steps: [
        { id: "run-warmup", label: "Mise en route", detail: "10 min très facile", kind: "duration", durationMinutes: 10 },
        { id: "run-main", label: "Allure aisée", detail: "30 min en endurance", kind: "duration", durationMinutes: 30 },
        { id: "run-cooldown", label: "Retour au calme", detail: "5 min de marche", kind: "duration", durationMinutes: 5 },
      ],
    },
  ],
  measurements: [
    { id: "m1", type: "weight", label: "Poids", value: 80.2, unit: "kg", date: "2026-08-11" },
    { id: "m2", type: "weight", label: "Poids", value: 79.8, unit: "kg", date: "2026-08-18" },
    { id: "m3", type: "weight", label: "Poids", value: 79.5, unit: "kg", date: "2026-08-27" },
    { id: "m4", type: "weight", label: "Poids", value: 79.1, unit: "kg", date: "2026-09-05" },
    { id: "m5", type: "weight", label: "Poids", value: 78.7, unit: "kg", date: "2026-09-13" },
    { id: "m6", type: "weight", label: "Poids", value: 78.4, unit: "kg", date: "2026-09-21" },
    { id: "waist-1", type: "waist", label: "Tour de taille", value: 88, unit: "cm", date: "2026-09-21" },
  ],
  programs: [
    { id: "program-1", name: "Équilibre 8 semaines", sport: "Multisport", weeks: 8, status: "active", startedAt: "2026-09-07" },
  ],
  nutritionHistory: [
    { date: "2026-09-16", label: "Mer", calories: 2140, target: 2200 },
    { date: "2026-09-17", label: "Jeu", calories: 2280, target: 2200 },
    { date: "2026-09-18", label: "Ven", calories: 2030, target: 2200 },
    { date: "2026-09-19", label: "Sam", calories: 2360, target: 2200 },
    { date: "2026-09-20", label: "Dim", calories: 2180, target: 2200 },
    { date: "2026-09-21", label: "Lun", calories: 2090, target: 2200 },
    { date: "2026-09-22", label: "Mar", calories: 1480, burnedCalories: 540, target: 2740 },
  ],
  nutritionEntries: [
    { id: "meal-breakfast", date: "2026-09-22", label: "Porridge, yaourt et fruits", mealType: "breakfast", source: "repas", sourceKind: "meal", sourceId: "repas-breakfast-22", quantity: 1, unit: "portion", calories: 430, protein: 25, carbs: 55, fat: 12, included: true },
    { id: "meal-lunch", date: "2026-09-22", label: "Poulet, riz et légumes", mealType: "lunch", source: "repas", sourceKind: "meal", sourceId: "repas-lunch-22", quantity: 1, unit: "portion", calories: 650, protein: 38, carbs: 72, fat: 22, included: true },
    { id: "meal-snack", date: "2026-09-22", label: "Goûter familial", mealType: "snack", source: "repas", sourceKind: "meal", sourceId: "repas-snack-22", quantity: 1, unit: "portion", calories: 280, protein: 8, carbs: 40, fat: 10, included: false },
    { id: "meal-dinner", date: "2026-09-22", label: "Soupe, tartines et fromage", mealType: "dinner", source: "repas", sourceKind: "meal", sourceId: "repas-dinner-22", quantity: 1, unit: "portion", calories: 400, protein: 19, carbs: 41, fat: 20, included: true }
  ],
  recentNutritionEntries: [
    { id: "recent-banana", date: "2026-09-21", label: "Banane", mealType: "snack", source: "repas", sourceKind: "catalog", sourceId: "food-banana", sourceVersion: "1", quantity: 120, unit: "g", calories: 106.8, protein: 1.3, carbs: 27.4, fat: 0.4, included: true },
    { id: "recent-cookie", date: "2026-09-20", label: "Cookie", mealType: "snack", source: "manual", sourceKind: "one_off", quantity: 1, unit: "piece", calories: 180, protein: 2.5, carbs: 24, fat: 8, included: true },
  ],
  foodCatalog: [
    { id: "food-banana", version: "1", label: "Banane", calories: 89, protein: 1.1, carbs: 22.8, fat: 0.3, nutrients: { fiber: 2.6, vitaminC: 8.7, magnesium: 27, potassium: 358 }, basisQuantity: 100, basisUnit: "g" },
    { id: "food-almonds", version: "1", label: "Amandes", calories: 579, protein: 21.2, carbs: 21.6, fat: 49.9, nutrients: { fiber: 12.5, vitaminE: 25.6, calcium: 269, iron: 3.7, magnesium: 270, potassium: 733, zinc: 3.1 }, basisQuantity: 100, basisUnit: "g" },
  ],
  sports: ["Musculation", "Natation", "Course", "Escalade", "Vélo", "Marche", "Yoga", "Multisport"],
  sportCatalog: [
    { id: "strength", name: "Musculation", icon: "dumbbell", isSystem: true, selected: true, measurements: [{ id: "reps", label: "Répétitions", format: "number", recordRule: "highest" }, { id: "load", label: "Charge", format: "number", unit: "kg", recordRule: "highest" }] },
    { id: "swimming", name: "Natation", icon: "waves", isSystem: true, selected: true, measurements: [{ id: "distance", label: "Distance", format: "distance", unit: "m", recordRule: "highest" }, { id: "duration", label: "Durée", format: "duration", unit: "min", recordRule: "lowest" }] },
    { id: "running", name: "Course", icon: "activity", isSystem: true, selected: true, measurements: [{ id: "distance", label: "Distance", format: "distance", unit: "km", recordRule: "highest" }, { id: "pace", label: "Allure", format: "duration", unit: "min/km", recordRule: "lowest" }] },
    { id: "climbing", name: "Escalade", icon: "mountain", selected: true, measurements: [{ id: "routes", label: "Voies", format: "number", recordRule: "highest" }, { id: "grade", label: "Cotation", format: "scale", recordRule: "highest" }] },
  ],
  exercises: [
    { id: "ex-squat", sportId: "strength", name: "Squat", category: "Jambes", equipment: "Barre", resultFormat:"strength",primaryMeasurementId:"load",aliases:[],measurementIds: ["reps", "load"], tags: ["force"], favorite: true, createdBy: "Catalogue système",version:1 },
    { id: "ex-bench", sportId: "strength", name: "Développé couché", category: "Pectoraux", equipment: "Barre", resultFormat:"strength",primaryMeasurementId:"load",aliases:["Bench press"],measurementIds: ["reps", "load"], tags: ["force"], createdBy: "Catalogue système",version:1 },
  ],
  sessionTemplates: [],
  recurrenceSeries: [],
  dailyActivity: [{ date: "2026-09-22", steps: 6420, activeCalories: 540, sleepMinutes: 446 }],
  personalRecords: [],
  activeWorkout: null,
  integrationStatus: { identity: "available", repas: "available", repasLastSuccess: "2026-09-22T20:00:00Z" },
  features: { assistantConfirmationRequired: false },
  restTimerAutoStart: true,
  includeActiveCaloriesInBudget: true,
  weeklyReport: { from: "2026-09-21", to: "2026-09-27", averageCalories: 1785, trackedDays: 2, targetCalories: 2200, completedSessions: 0, plannedSessions: 3, skippedSessions: 0, overdueSessions: 0, trainingMinutes: 0, distanceMeters: 0, volumeKg: 0, totalSteps: 6420, averageSteps: 6420, stepDays: 1, totalActiveCalories: 540, averageActiveCalories: 540, averageSleepMinutes: 446, sleepDays: 1, measurements: [{ id: "m6", type: "weight", label: "Poids", value: 78.4, unit: "kg", date: "2026-09-21" }] },
};
